/**
 * Main color palette object containing color definitions with shade variants.
 * Each color should have numeric shade keys (100, 200, 300, etc.) with hex color values.
 * 
 * @example
 * colors = {
 *   primary: {
 *     100: '#e3f2fd',
 *     400: '#2196f3',
 *     700: '#1976d2'
 *   }
 * }
 */

const ivory = {
	400: '#F7F3E6',
	500: '#CECAC0',
	600: '#A49D8C',
	700: '#7E7A69',
	800: '#423B3D',
};
const blue = {
	100: '#CAE4ED',
	200: '#5FACCA',
	400: '#0077AA',
	800: '#005275',
	900: '#002432',
};
const white = {
	100: '#FFFFFF',
};

export const colors = {
	ivory,
	blue,
	white,
	primary: {
		...ivory,
		main: ivory[700],
	}
};

/**
 * Mapping of human-readable shade names to their corresponding numeric values.
 * Allows using semantic names instead of numbers when calling getColor().
 * 
 * @type {Object<string, number>}
 */
const shadeNames = {
	lighter: 100,
	light: 300,
	regular: 400,
	bold: 700,
	heavy: 900
};

/**
 * Converts a hexadecimal color string to RGB object.
 * 
 * @param {string} hex - Hexadecimal color string (with or without #)
 * @returns {Object|null} RGB object with r, g, b properties, or null if invalid
 * @private
 */
const hexToRgb = (hex) => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result ? {
		r: parseInt(result[1], 16),
		g: parseInt(result[2], 16),
		b: parseInt(result[3], 16)
	} : null;
};

/**
 * Converts RGB color values to hexadecimal color string.
 * 
 * @param {number} r - Red component (0-255)
 * @param {number} g - Green component (0-255)
 * @param {number} b - Blue component (0-255)
 * @returns {string} Hexadecimal color string with # prefix
 * @private
 */
const rgbToHex = (r, g, b) => {
	return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Interpolates between two hex colors based on a factor.
 * Used to generate intermediate shade values when exact shade doesn't exist.
 * 
 * @param {string} color1 - Starting hex color
 * @param {string} color2 - Ending hex color
 * @param {number} factor - Interpolation factor (0-1, where 0=color1, 1=color2)
 * @returns {string} Interpolated hex color
 * @private
 */
const interpolateColor = (color1, color2, factor) => {
	const rgb1 = hexToRgb(color1);
	const rgb2 = hexToRgb(color2);
	
	if (!rgb1 || !rgb2) return color1;
	
	const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * factor);
	const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * factor);
	const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * factor);
	
	return rgbToHex(r, g, b);
};

/**
 * Retrieves a color from the palette with optional shade specification.
 * Supports both numeric shades and named shades. If the exact shade doesn't exist,
 * it will interpolate between the nearest available shades.
 * 
 * @param {string} name - The color name from the colors object
 * @param {number|string} [shade=400] - Numeric shade (100-900) or named shade ('lighter', 'light', etc.)
 * @returns {string} Hex color string, or fallback color if not found
 * 
 * @example
 * getColor('primary') // returns primary-400
 * getColor('primary', 700) // returns primary-700
 * getColor('primary', 'bold') // returns primary-700 (maps to bold)
 * getColor('primary', 350) // interpolates between nearest shades
 */
export const getColor = (name, shade = 400) => {
	// Handle shade names
	const numericShade = typeof shade === 'string' ? shadeNames[shade] || 400 : shade;
	
	// Check if the color exists
	if (!colors[name]) {
		console.warn(`Color "${name}" not found`);
		return '#000000';
	}
	
	// If the exact shade exists, return it
	if (colors[name][numericShade]) {
		return colors[name][numericShade];
	}
	
	// Handle intermediate shade values by blending
	const availableShades = Object.keys(colors[name]).map(Number).sort((a, b) => a - b);
	
	// Find the two nearest shades
	let lowerShade = null;
	let upperShade = null;
	
	for (let i = 0; i < availableShades.length; i++) {
		if (availableShades[i] <= numericShade) {
			lowerShade = availableShades[i];
		}
		if (availableShades[i] >= numericShade && upperShade === null) {
			upperShade = availableShades[i];
		}
	}
	
	// If we have both bounds, interpolate
	if (lowerShade !== null && upperShade !== null && lowerShade !== upperShade) {
		const factor = (numericShade - lowerShade) / (upperShade - lowerShade);
		return interpolateColor(colors[name][lowerShade], colors[name][upperShade], factor);
	}
	
	// If we only have a lower bound, return it
	if (lowerShade !== null) {
		return colors[name][lowerShade];
	}
	
	// If we only have an upper bound, return it
	if (upperShade !== null) {
		return colors[name][upperShade];
	}
	
	// Fallback to the first available shade
	return colors[name][availableShades[0]] || '#000000';
};